import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(isCollapsed);

  useEffect(() => {
    setInternalCollapsed(isCollapsed);
  }, [isCollapsed]);

  const isReseller = userProfile?.role === 'reseller';

  const defaultNavigationItems = [
    { path: '/dashboard-home', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/content-editor', label: 'Content Editor', icon: 'FileEdit' },
    { path: '/page-manager', label: 'Page Manager', icon: 'Files' },
    { path: '/media-library', label: 'Media Library', icon: 'Image' },
    { path: '/seo-studio', label: 'SEO Studio', icon: 'TrendingUp' },
    { path: '/analytics-hub', label: 'Analytics Hub', icon: 'BarChart3' },
    { path: '/template-library', label: 'Templates', icon: 'Layout' },
    { path: '/version-control', label: 'Version Control', icon: 'GitBranch' },
    { path: '/settings-central', label: 'Settings', icon: 'Settings' },
  ];

  const resellerNavigationItems = [
    { path: '/reseller-portal', label: 'My Portal', icon: 'Building2' },
    { path: '/reseller-websites', label: 'Websites', icon: 'Globe' },
    { path: '/reseller-clients', label: 'Team Members', icon: 'Users' },
  ];

  const navigationItems = isReseller ? resellerNavigationItems : defaultNavigationItems;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleToggleCollapse = () => {
    const newState = !internalCollapsed;
    setInternalCollapsed(newState);
    if (onToggleCollapse) {
      onToggleCollapse(newState);
    }
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <button
        onClick={handleMobileToggle}
        className="mobile-menu-button"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={20} />
      </button>
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`sidebar ${internalCollapsed ? 'collapsed' : ''} ${
          isMobileOpen ? 'mobile-open' : ''
        }`}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Zap" size={24} />
          </div>
          <span className="sidebar-brand">webpages123.com</span>
        </div>

        {isReseller && (
          <div className="px-3 py-2 mx-2 mb-1 rounded-lg bg-primary/10">
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={14} className="text-primary" />
              <span className="text-xs font-medium text-primary sidebar-nav-text">Reseller Portal</span>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {navigationItems?.map((item) => (
            <div
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`sidebar-nav-item ${
                location?.pathname === item?.path ? 'active' : ''
              }`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  handleNavigation(item?.path);
                }
              }}
            >
              <Icon name={item?.icon} size={20} />
              <span className="sidebar-nav-text">{item?.label}</span>
            </div>
          ))}
        </nav>

        <button
          onClick={handleToggleCollapse}
          className="sidebar-toggle hidden lg:flex"
          aria-label={internalCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={internalCollapsed ? 'ChevronRight' : 'ChevronLeft'}
            size={16}
          />
        </button>
      </aside>
    </>
  );
};

export default Sidebar;